import { mapText } from "./map-i18n.js";
import {
  MAP_TUTORIALS,
  normalizedTutorialStep,
} from "./map-tutorial-contract.js";
import { provenanceIndicatorSampleMarkup } from "../js/components/provenance-indicator.js";

const TOKEN_START = "\uE000";
const TOKEN_END = "\uE001";
const TOKEN_PATTERN = /\uE000([A-Za-z0-9_]+)\uE001/g;

function escapeHtml(value) {
  return String(value ?? "").replace(
    /[&<>\"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char] || char,
  );
}

function translated([key, fallback], vars = {}) {
  const value = mapText(key, vars);
  return value === `map.${key}`
    ? String(fallback).replace(/\{\s*\$([A-Za-z0-9_]+)\s*\}/g, (_match, name) =>
        Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : "",
      )
    : value;
}

function itemIconUrl(itemId) {
  const resolver = globalThis.window?.__fishystuffResolveFishItemIconUrl;
  if (typeof resolver !== "function") {
    return "";
  }
  return String(resolver(itemId) || "").trim();
}

function tutorialTokenMarkup(token) {
  const label = String(token?.label || "").trim();
  switch (String(token?.kind || "").trim()) {
    case "item": {
      const iconUrl = itemIconUrl(token.itemId);
      return `<span class="mx-0.5 inline-flex max-w-full items-center gap-1 rounded border border-base-300 bg-base-100 px-1.5 py-0.5 align-baseline font-medium text-base-content" data-tutorial-item-id="${escapeHtml(
        token.itemId,
      )}">${
        iconUrl
          ? `<img class="size-4 shrink-0 object-contain" src="${escapeHtml(
              iconUrl,
            )}" alt="" aria-hidden="true">`
          : ""
      }<span>${escapeHtml(label)}</span></span>`;
    }
    case "icon":
      return `<span class="mx-0.5 inline-flex max-w-full items-center gap-1 rounded border border-base-300 bg-base-100 px-1.5 py-0.5 align-baseline font-medium text-base-content"><svg class="fishy-icon size-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-${escapeHtml(
        token.icon,
      )}"></use></svg><span>${escapeHtml(label)}</span></span>`;
    case "code":
      return `<code class="mx-0.5 break-all rounded bg-base-300/70 px-1 py-0.5 text-[0.8em] text-base-content">${escapeHtml(
        label,
      )}</code>`;
    case "provenance":
      return `<span class="mx-0.5 inline-flex max-w-full items-center gap-1.5 rounded border border-base-300 bg-base-100 px-1.5 py-0.5 align-baseline font-medium text-base-content">${provenanceIndicatorSampleMarkup(
        {
          label,
          tone: token.tone,
        },
      )}<span>${escapeHtml(label)}</span></span>`;
    default:
      return escapeHtml(label);
  }
}

function richCopyMarkup(copy) {
  if (Array.isArray(copy)) {
    return escapeHtml(translated(copy));
  }
  const tokens =
    copy?.tokens && typeof copy.tokens === "object" ? copy.tokens : {};
  const variables = Object.fromEntries(
    Object.keys(tokens).map((name) => [
      name,
      `${TOKEN_START}${name}${TOKEN_END}`,
    ]),
  );
  const source = translated(copy?.message || ["", ""], variables);
  let cursor = 0;
  let markup = "";
  for (const match of source.matchAll(TOKEN_PATTERN)) {
    markup += escapeHtml(source.slice(cursor, match.index));
    markup += tutorialTokenMarkup(tokens[match[1]]);
    cursor = Number(match.index) + match[0].length;
  }
  return `${markup}${escapeHtml(source.slice(cursor))}`;
}

function activeStepBody(step, progress) {
  const phase =
    step.action === "cycle-query-window"
      ? progress.windowCyclePhase
      : step.action === "cycle-layer-collapse"
        ? progress.collapseCyclePhase
        : "";
  if (phase && step.phaseBodies?.[phase]) {
    return step.phaseBodies[phase];
  }
  if (
    step.action === "show-hotspots-layer" &&
    progress.hotspotsFilteredByQuery === true
  ) {
    return step.filteredBody || step.body;
  }
  if (
    step.action === "show-hotspots-layer" &&
    progress.hotspotsHidden === true
  ) {
    return step.resumeBody || step.body;
  }
  if (
    step.action === "cycle-fish-evidence-settings" &&
    progress.fishEvidenceSettingsClosed === true
  ) {
    return step.resumeBody || step.body;
  }
  if (
    step.action === "detach-regions-clip" &&
    progress.regionsClipInitiallyDetached === true &&
    progress.regionsClipAttached !== true
  ) {
    return step.detachedBody || step.body;
  }
  return step.body;
}

function selectionCorrectionMarkup(step, progress) {
  if (
    step.action === "inspect-hotspot" &&
    progress.selectionRejected === "not-hotspot"
  ) {
    return `<p class="m-0 mt-2 text-xs font-medium text-warning" role="status" data-tutorial-live-guidance="not-hotspot">${richCopyMarkup(
      step.rejectedSelectionBody,
    )}</p>`;
  }
  if (
    step.action !== "select-map-point" ||
    progress.selectionRejected !== "landmark"
  ) {
    return "";
  }
  const copy = [
    "tutorial.selection.landmark_rejected",
    "That is a landmark, sample, NPC, or hotspot. Click open water in a fishing zone instead.",
  ];
  return `<p class="m-0 mt-2 text-xs font-medium text-warning" role="status" data-tutorial-live-guidance="landmark">${escapeHtml(
    translated(copy),
  )}</p>`;
}

function scrollInstructionMarkup(progress) {
  if (!progress.scrollDirection) {
    return "";
  }
  const copy =
    progress.scrollDirection === "up"
      ? [
          "tutorial.scroll_up",
          "Scroll up inside Layers to reach the highlighted control.",
        ]
      : [
          "tutorial.scroll_down",
          "Scroll down inside Layers to reach the highlighted control.",
        ];
  return `<p class="m-0 mt-2 text-xs font-medium text-primary" role="status" data-tutorial-live-guidance="scroll-${escapeHtml(
    progress.scrollDirection,
  )}">${escapeHtml(
    translated(copy),
  )}</p>`;
}

function hideTutorialControl(step) {
  if (step.action !== "hide-and-resume-tutorial") {
    return "";
  }
  return `<button class="btn btn-primary btn-sm mt-3" type="button" data-tutorial-hide>${escapeHtml(
    translated(["tutorial.hide", "Hide tutorial"]),
  )}</button>`;
}

function tutorialProceedControl(step) {
  if (step.allowProceed !== true) {
    return "";
  }
  return `<button class="btn btn-primary btn-sm mt-3" type="button" data-tutorial-proceed>${escapeHtml(
    translated(["tutorial.proceed", "Proceed"]),
  )}</button>`;
}

function tutorialStepLeadMarkup(stepIndex, { active = false } = {}) {
  return `
    <span
      class="inline-flex w-5 shrink-0 items-center justify-end ${
        active ? "text-primary" : "text-base-content/45"
      }"
      data-tutorial-step-number="${stepIndex + 1}"
    >
      <span class="min-w-3 text-right text-xs font-bold tabular-nums">${stepIndex + 1}</span>
    </span>
  `;
}

export function renderMapTutorialStage(
  lessonId,
  rawStep,
  { completed = false, expandedSteps = null, progress = {} } = {},
) {
  const normalizedLessonId = String(lessonId ?? "").trim();
  if (!normalizedLessonId) {
    return "";
  }
  const tutorial = MAP_TUTORIALS[normalizedLessonId];
  const exitControl = ({ completion = false } = {}) =>
    `<button class="btn btn-ghost btn-xs shrink-0" type="button" data-tutorial-exit>${
      completion
        ? '<svg class="fishy-icon size-4 shrink-0 text-success" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-completion-stamp"></use></svg>'
        : ""
    }<span>${escapeHtml(
      completion
        ? translated(["tutorial.complete", "Complete"])
        : translated(["tutorial.exit", "Exit tutorial"]),
    )}</span></button>`;
  if (!tutorial) {
    return `
      <article class="card card-sm card-border pointer-events-auto bg-base-100 shadow-xl">
        <div class="card-body gap-3 p-3">
          <div class="flex items-start justify-between gap-3">
            <h3 class="card-title text-sm">${escapeHtml(
              translated(["tutorial.unavailable_title", "Tutorial unavailable"]),
            )}</h3>
            ${exitControl()}
          </div>
          <p class="m-0 text-sm text-base-content/75">${escapeHtml(
            translated([
              "tutorial.unavailable_body",
              "This tutorial is not part of the current map guide.",
            ]),
          )}</p>
        </div>
      </article>
    `;
  }

  const stepIndex = normalizedTutorialStep(rawStep, tutorial.steps.length);
  const currentStep = tutorial.steps[stepIndex];
  const exit = exitControl({
    completion: completed && stepIndex === tutorial.steps.length - 1,
  });
  const currentBody = activeStepBody(currentStep, progress);
  const expandedStepIndexes =
    expandedSteps === null
      ? new Set(
          completed
            ? [stepIndex]
            : stepIndex > 0
              ? [stepIndex - 1]
              : [],
        )
      : new Set(Array.from(expandedSteps, Number));
  const tasks = tutorial.steps
    .slice(0, stepIndex + 1)
    .map((task, index) => {
      const taskCompleted = completed || index < stepIndex;
      const taskBody = index === stepIndex ? currentBody : task.body;
      if (!taskCompleted) {
        return `
          <li>
            <section
              class="rounded-box border border-base-300 bg-base-200 px-3 py-3"
              data-tutorial-task
              data-tutorial-step="${index}"
              aria-current="step"
            >
              <div class="flex items-center gap-2">
                ${tutorialStepLeadMarkup(index, { active: true })}
                <h3 id="fishymap-tutorial-step-title" class="m-0 min-w-0 flex-1 text-sm font-semibold text-base-content">${escapeHtml(
                  translated(task.title),
                )}</h3>
                <span class="inline-flex shrink-0 text-primary" data-tutorial-current-step>
                  <svg class="fishy-icon size-4" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-large-arrow-left-fill"></use></svg>
                </span>
              </div>
              <div class="pl-7">
                <p class="m-0 mt-2 text-sm leading-6 text-base-content/75">${richCopyMarkup(
                  taskBody,
                )}</p>
                ${selectionCorrectionMarkup(task, progress)}
                ${scrollInstructionMarkup(progress)}
                ${hideTutorialControl(task)}
                ${tutorialProceedControl(task)}
              </div>
            </section>
          </li>
        `;
      }
      const open = expandedStepIndexes.has(index) ? " open" : "";
      return `
        <li>
          <details
            class="collapse rounded-box border border-base-300/70 bg-base-200/70"
            data-tutorial-task
            data-tutorial-step="${index}"
            ${open}
          >
            <summary class="collapse-title flex min-h-0 cursor-pointer items-center gap-2 px-3 py-2 text-sm">
              ${tutorialStepLeadMarkup(index)}
              <span class="min-w-0 flex-1 font-semibold text-base-content/60 line-through decoration-1">${escapeHtml(
                translated(task.title),
              )}</span>
              <span class="inline-flex shrink-0 text-success">
                <svg class="fishy-icon size-4" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="#fishy-check-badge-solid"></use></svg>
                <span class="sr-only">${escapeHtml(
                  translated(["tutorial.task_completed", "Completed"]),
                )}</span>
              </span>
            </summary>
            <div class="collapse-content pl-10 pr-3 pb-3 text-sm leading-6 text-base-content/65">
              <p class="m-0">${richCopyMarkup(taskBody)}</p>
            </div>
          </details>
        </li>
      `;
    })
    .join("");

  return `
    <article
      class="card card-sm card-border pointer-events-auto max-h-96 overflow-hidden bg-base-100 shadow-xl"
      aria-label="${escapeHtml(translated(tutorial.title))}"
    >
      <div class="card-body min-h-0 gap-2 p-3">
        <div class="flex justify-end">
          ${exit}
        </div>
        <ol
          class="m-0 min-h-0 max-h-72 space-y-2 overflow-y-auto overscroll-contain p-0 pr-1"
          data-tutorial-task-list
        >
          ${tasks}
        </ol>
      </div>
    </article>
  `;
}
